import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  clampPage,
  pageCountOf,
  slicePage,
} from './pagination'

/**
 * Client-side paging for an in-memory list. Resets to page 1 when `resetKey` changes.
 */
export function useClientPagination<T>(
  items: readonly T[],
  opts: { pageSize?: number; resetKey?: string | number } = {},
) {
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE
  const resetKey = opts.resetKey ?? ''
  const [page, setPageRaw] = useState(1)
  const [seenKey, setSeenKey] = useState(resetKey)

  if (seenKey !== resetKey) {
    setSeenKey(resetKey)
    setPageRaw(1)
  }

  const total = items.length
  const pageCount = pageCountOf(total, pageSize)
  const page_ = clampPage(page, pageCount)

  const rows = useMemo(
    () => slicePage(items, page_, pageSize),
    [items, page_, pageSize],
  )

  return {
    page: page_,
    pageCount,
    pageSize,
    total,
    rows,
    setPage: (next: number) => setPageRaw(clampPage(next, pageCount)),
  }
}
